import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';
import { Clip } from '@/types/clip.types';
import { StatusBadge } from '@/components/StatusBadge';
import { AudioPlayer, AudioPlayerHandle } from '@/components/AudioPlayer';
import { getFtpStreamUrl } from '@/services/api';
import './MobileClipCard.css';

interface MobileClipCardProps {
  clip: Clip;
  position: number;
  /** Whether this clip is currently playing (controlled by parent playback controller) */
  isPlaying?: boolean;
  /** Notify parent when play state changes */
  onPlayStateChange?: (clipId: string, playing: boolean) => void;
}

const formatDuration = (seconds: number | null | undefined): string => {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const MobileClipCard: React.FC<MobileClipCardProps> = ({
  clip,
  position,
  isPlaying = false,
  onPlayStateChange,
}) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const audioRef = useRef<AudioPlayerHandle>(null);

  const hasAudio = clip.audioPath && ['audio_ready', 'audio_approved'].includes(clip.status);

  // Load audio URL on demand (when user expands the card)
  useEffect(() => {
    if (!expanded || !hasAudio || audioUrl || audioLoading) return;
    setAudioLoading(true);
    getFtpStreamUrl(clip.audioPath!, clip.companyId)
      .then(setAudioUrl)
      .catch(() => setAudioUrl(null))
      .finally(() => setAudioLoading(false));
  }, [expanded, hasAudio, audioUrl, audioLoading, clip.audioPath, clip.companyId]);

  const handleToggleExpand = () => {
    if (hasAudio) {
      setExpanded(prev => !prev);
    }
  };

  return (
    <div className="pm_mclip-card">
      {/* Main row — always visible */}
      <div
        className={`pm_mclip-row ${hasAudio ? 'tappable' : ''}`}
        onClick={handleToggleExpand}
      >
        {/* Position */}
        <div className="pm_mclip-position">{position}</div>

        {/* Content */}
        <div className="pm_mclip-info">
          <p className="pm_mclip-title">{clip.articleTitle}</p>
          <div className="pm_mclip-meta">
            <StatusBadge status={clip.status} />
            {clip.duration && (
              <span className="pm_mclip-duration">{formatDuration(clip.duration)}</span>
            )}
          </div>
        </div>

        {/* Play indicator (only when audio exists) */}
        {hasAudio && (
          <div className="pm_mclip-play-hint">
            {expanded ? <Pause size={16} /> : <Play size={16} />}
          </div>
        )}
      </div>

      {/* Expanded audio player */}
      {expanded && hasAudio && (
        <div className="pm_mclip-audio">
          {audioLoading ? (
            <div className="pm_mclip-audio-loading">
              <Loader2 size={14} className="pm_mclip-spinner" />
              <span>Loading audio...</span>
            </div>
          ) : audioUrl ? (
            <AudioPlayer
              src={audioUrl}
              ref={audioRef}
              onPlay={() => onPlayStateChange?.(clip.id, true)}
              onPause={() => onPlayStateChange?.(clip.id, false)}
              onEnded={() => onPlayStateChange?.(clip.id, false)}
            />
          ) : (
            <span className="pm_mclip-audio-loading">Audio not available</span>
          )}
        </div>
      )}
    </div>
  );
};
